<template>
  <v-app :theme="theme">
    <ZPWAUpdatePrompt :register="pwaRegister" :title="$t('messages.info.pwaUpdateTitle')">
      <template #default="{ update, close }">
        <div class="text-body-2 mb-3">{{ $t('messages.info.pwaUpdateMessage') }}</div>
        <div class="d-flex justify-end ga-2">
          <v-btn variant="text" color="primary" prepend-icon="mdi-arrow-up-bold" @click="reloadApp(update)">
            {{ $t('common.actions.reload') }}
          </v-btn>
          <v-btn variant="text" @click="close">{{ $t('common.actions.later') }}</v-btn>
        </div>
      </template>
    </ZPWAUpdatePrompt>
    <div id="tourFirstAnchor" />
    <Transition name="app">
      <LoadingComponent v-if="false" />
    </Transition>

    <LeftBarComponent v-if="!router.currentRoute.value.meta.fullPage" :layers="karonteLayers" />
    <v-main>
      <v-dialog v-model="showSplash" width="900" persistent class="splash-dialog">
        <Splash @close="closeSplash" />
      </v-dialog>
      <v-dialog v-model="showNoOrgDialog" max-width="500">
        <v-card>
          <v-card-title>{{ $t('views.noOrganization.title') }}</v-card-title>
          <v-card-text>{{ $t('views.noOrganization.message') }}</v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn color="primary" @click="showNoOrgDialog = false">{{ $t('common.actions.ok') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <VOnboardingWrapper ref="tourWrapper" :steps="tourSteps" @exit="endTour">
        <template #default="{ previous, next, step, exit, isFirst, isLast }">
          <VOnboardingStep>
            <v-card style="max-width: 50vw">
              <v-card-title class="text-h5">
                {{ step.content.title }}
              </v-card-title>

              <!-- eslint-disable-next-line vue/no-v-html, vue/no-v-text-v-html-on-component -->
              <v-card-text v-html="step.content.text"></v-card-text>

              <v-checkbox v-model="hideTour" :label="$t('common.concepts.dontShowAgain')" hide-details />

              <v-card-actions>
                <v-btn v-if="!isFirst" :text="$t('common.actions.previous')" variant="text" @click="previous"></v-btn>
                <v-btn v-if="!isLast" :text="$t('common.actions.next')" variant="text" @click="next"></v-btn>
                <v-btn :text="$t('common.actions.finish')" variant="text" @click="exit"></v-btn>
              </v-card-actions>
            </v-card>
          </VOnboardingStep>
        </template>
      </VOnboardingWrapper>

      <!-- Full page routes -->
      <template v-if="router.currentRoute.value.meta.fullPage">
        <AppBar></AppBar>
        <!--        <router-view></router-view>-->
        <router-view v-slot="{ Component }">
          <div class="d-flex">
            <transition name="slide-left" mode="out-in">
              <component :is="Component" />
            </transition>
            <transition name="slide-right" mode="out-in">
              <DynamicFilters
                v-if="filtersOpen"
                v-model="karonteLayers"
                class="dashboard-right-container"
                :class="{ 'is-mobile': isMobileRight }" />
            </transition>
          </div>
        </router-view>
      </template>

      <!-- Map layout -->
      <div v-show="!router.currentRoute.value.meta.fullPage" class="map-container" :theme="theme">
        <MainMap id="mainMap" ref="map" :layers="karonteLayers" @view-loaded="view = $event">
          <template #left-menu>
            <router-view v-slot="{ Component, route }">
              <transition name="slide-left" mode="out-in">
                <div v-if="Component && route.path !== '/'" :key="route.path" class="left-container" :class="{ 'is-mobile': isMobileLeft }">
                  <component :is="Component" />
                </div>
              </transition>
            </router-view>
          </template>
          <template #right-menu>
            <router-view v-slot="{ route }">
              <transition name="slide-right" mode="out-in">
                <RightBarContainer v-if="route.query.incident" class="right-container" :class="{ 'is-mobile': isMobileRight }" />
              </transition>
            </router-view>
            <transition name="slide-right" mode="out-in">
              <DynamicFilters v-if="filtersOpen" v-model="karonteLayers" class="right-container" :class="{ 'is-mobile': isMobileRight }" />
            </transition>
          </template>
        </MainMap>
      </div>
      <UserAdmin v-if="showUserAdminPanel" />
    </v-main>
  </v-app>
</template>

<script setup lang="ts">
//Framework Import
import type { ShallowReactive } from 'vue'
import { ref, shallowRef, provide, computed, onBeforeMount, onMounted } from 'vue'

//Karonte Import
import { storeToRefs } from 'pinia'

import { datadogRum } from '@datadog/browser-rum'
import { VOnboardingWrapper, VOnboardingStep, useVOnboarding } from 'v-onboarding'
import { useRegisterSW } from 'virtual:pwa-register/vue'
import { useDisplay } from 'vuetify'

import type { Layer } from '@tsyl/karonte/lib/types/layers'
import type { View } from '@tsyl/karonte/lib/types/view'
import { ReactiveLayerBuilder } from '@tsyl/karonte/lib/utils/LayerBuilder'

// Tour

import { getCalculationTypes } from '@/services/api/weather'
import settingsStore from '@/stores/settingsStore.ts'
import emitter from '@/utils/EventBus'

import DynamicFilters from './components/Filter/DynamicFilters.vue'
import LeftBarComponent from './components/LeftBarComponent.vue'
import MainMap from './components/MainMap.vue'
import RightBarContainer from './components/RightBarComponent/RightBarContainer.vue'
import Splash from './components/Splash.vue'
import UserAdmin from './components/User/UserAdmin.vue'
import { getMeassurements } from './services/api/masterData'
import { getTourSteps } from './services/tour'

// Tracker

//Components Import

//Services Import

//Constants Import

// Store Imports
import AuthStore from './stores/auth'
import FiltersStore from './stores/FiltersStore'
import FobsStore from './stores/fobsStore'
import LangStore from './stores/langStore'

const filtersStore = FiltersStore()
const settings = settingsStore()
const fobsStore = FobsStore()
const langStore = LangStore()
const { waitUntilInitialised } = AuthStore()
const { theme } = storeToRefs(settings)
const { filtersOpen } = storeToRefs(filtersStore)

const { lang } = storeToRefs(langStore)
const authStore = AuthStore()
const { showUserAdminPanel, user, isAuthenticated, org } = storeToRefs(authStore)

const view = ref<View>()
const showSplash = ref(false)
const showNoOrgDialog = ref(false)

const pwaRegister = useRegisterSW({
  onRegisterError(error) {
    console.error('Service worker registration failed', error)
  }
})

async function reloadApp(update: (reloadPage?: boolean) => Promise<void>) {
  // Skip the auto-reload that updateServiceWorker(true) does — it preserves the
  // current URL, which on /-rooted views would land users on the public landing
  // page instead of the app. Explicitly navigate to the PWA scope.
  await update(false)
  window.location.href = import.meta.env.BASE_URL
}

import { useRouter } from 'vue-router'

import { LEFT_PANNEL_WIDTH, RIGHT_PANNEL_WIDTH } from './constants/layout'

import LoadingComponent from '@/components/LoadingComponent.vue'
import AppBar from '@/components/AppBar.vue'
import type { OrganizationLayersTOC } from '@/types/OrganizationLayers.ts'
import { non_authenticated } from '@/constants/layers'
import { setHeatDetectionFilterArea } from '@/constants/layers/hotspots/LivingAtlasHeatDetection'
import { getUserAccessLevel } from '@/services/api/user'
import { computeFilterGeometry } from '@/services/areas'
const router = useRouter()

const { width } = useDisplay()
const loadingApp = ref(false)

const tourWrapper = ref(null)
const { start, finish } = useVOnboarding(tourWrapper)
const hideTour = ref(false)
emitter.on('startTour', start)

const karonteLayers = shallowRef<ShallowReactive<Layer[]>>(ReactiveLayerBuilder([]))

const isLeftPannelOpen = computed(() => {
  return router.currentRoute.value.name !== 'default'
})

const isRightPannelOpen = computed(() => {
  return Boolean(router.currentRoute.value.query.incident)
})

const isMobileLeft = computed(() => {
  // TODO MAYBE IN CONSTS
  let maxWidth = 1085

  if (!isRightPannelOpen.value) maxWidth -= RIGHT_PANNEL_WIDTH
  return width.value < maxWidth
})

const isMobileRight = computed(() => {
  // TODO MAYBE IN CONSTS
  let maxWidth = 1085

  if (!isLeftPannelOpen.value) maxWidth -= LEFT_PANNEL_WIDTH
  return width.value < maxWidth
})

const rightBarWidth = computed(() => {
  return isMobileRight.value ? '100%' : `${RIGHT_PANNEL_WIDTH}px`
})

const leftBarWidth = computed(() => {
  return isMobileLeft.value ? '100%' : `${LEFT_PANNEL_WIDTH}px`
})

const tourSteps = computed(() => getTourSteps(lang.value))
const endTour = () => {
  finish()
  if (hideTour.value) {
    window.localStorage.setItem('hideTour', 'true')
  }
}

const closeSplash = () => {
  showSplash.value = false
  if (!window.localStorage.getItem('hideTour')) start()
}
// call to cache data
getMeassurements()

provide(
  'karonteLayers',
  computed(() => karonteLayers)
)
provide(
  'view',
  computed(() => view.value)
)

onBeforeMount(() => {
  loadingApp.value = true
  return router.isReady().then(async () => {
    console.debug('Router ready, checking auth')
    await authStore.init()
    // Sync Vue Router's internal state with the URL cleanup done by init() via history.pushState.
    // Without this, the router still thinks ?code&state are in the current route and would copy
    // them to subsequent navigations via the beforeEach guard.
    const { code, state, error, error_description, ...cleanQuery } = router.currentRoute.value.query
    if (code || state || error || error_description) {
      await router.replace({ path: '/', query: cleanQuery })
    }
    const loggedIn = authStore.isAuthenticated
    console.debug('Auth initialised, logged in?', loggedIn)
    authStore.setInitialised(true)

    const authErrorDesc = (error_description as string) || ''
    if (authErrorDesc.includes('organization membership') || (loggedIn && !org.value)) {
      showNoOrgDialog.value = true
    }

    if (loggedIn) {
      // clear url params and go to /
      console.debug('Logged in')
      if (org.value?.datadog_config) {
        // if exists, format is app_id:token:site:false
        const cfg = org.value.datadog_config.split(':')
        if (cfg[3] === 'true') {
          console.debug('Init datadog')
          datadogRum.init({
            applicationId: cfg[0],
            clientToken: cfg[1],
            // `site` refers to the Datadog site parameter of your organization
            // see https://docs.datadoghq.com/getting_started/site/
            site: cfg[2],
            //  service: 'my-web-application',
            //  env: 'production',
            //  version: '1.0.0',
            sessionSampleRate: 100,
            sessionReplaySampleRate: 100,
            trackResources: true,
            trackLongTasks: true,
            trackUserInteractions: true,
            enablePrivacyForActionName: true
          })
        }
      } else console.debug('No datadog config or not enabled')
    }
    const toc: OrganizationLayersTOC[] = org?.value?.toc ?? non_authenticated.toc
    const allLayers = toc.map((categoryToc: OrganizationLayersTOC) => categoryToc.layers).flat()
    // Tenants whose layers are trimmed to their area (trimLayers) only get the heat detections
    // inside the areas they can access; anonymous users and other tenants see them worldwide.
    if (loggedIn && org.value?.trimLayers) {
      // If the hierarchy can't be fetched, show the layers unfiltered rather than no layers.
      const orgIds = await getUserAccessLevel().catch(() => [])
      setHeatDetectionFilterArea(computeFilterGeometry(orgIds))
    }
    karonteLayers.value = ReactiveLayerBuilder(allLayers)
  })
})

onMounted(async () => {
  fobsStore.requestMasterData()

  await getCalculationTypes()
  // Load layers that may require auth
  waitUntilInitialised().then(() => {
    // retrieve user settings
    if (isAuthenticated.value) {
      // overwritte app settings
      if (user.value) settings.updateSettings(user.value?.UserMetadata)
    }
    loadingApp.value = false
  })
  const splashDialogSeen = localStorage.getItem('DNSWelcomeDialog')
  if (!splashDialogSeen) showSplash.value = true

  if (!window.localStorage.getItem('hideTour') && !showSplash.value) start()
})
</script>

<style lang="scss">
html {
  overflow: hidden !important;
}

.left-container {
  position: relative;
  height: 100vh;
  background-color: rgb(var(--v-theme-surface));

  --left-panel-width: v-bind(leftBarWidth);
  width: var(--left-panel-width);

  &:empty {
    width: 0;
  }
  &.is-mobile {
    position: absolute;
    left: 0;
    z-index: 9;
  }
}

.right-container {
  --right-panel-width: v-bind(rightBarWidth) !important;
  width: var(--right-panel-width) !important;
  height: 100vh !important;
  background: rgb(var(--v-theme-background)) !important;
  z-index: 10 !important;
  display: flex !important;
  &.is-mobile {
    position: absolute !important;
    right: 0 !important;
  }
}

.dashboard-right-container {
  --right-panel-width: v-bind(rightBarWidth) !important;
  width: var(--right-panel-width) !important;
  height: calc(100vh - var(--v-layout-top)) !important;
  background: rgb(var(--v-theme-background)) !important;
  z-index: 10 !important;
  display: flex !important;
}

.widget-container.full-left,
.widget-container.full-right {
  justify-content: flex-start !important;
}

.slide-left-enter-active,
.slide-left-leave-active {
  transition: margin 0.3s ease;
}

.slide-left-enter-from,
.slide-left-leave-to {
  margin-left: calc(var(--left-panel-width) * -1);
}

/* we will explain what these classes do next! */
.app-enter-active,
.app-leave-active {
  transition: opacity 1s ease;
}

.app-enter-from,
.app-leave-to {
  opacity: 0;
}

#tourFirstAnchor {
  width: 1px;
  height: 1px;
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  background-color: rgb(var(--v-theme-dark-color));
  opacity: 0;
  visibility: hidden;
}

.z-pwa-toast {
  z-index: 2 !important;
  background: rgb(var(--v-theme-background));
}
</style>
